import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select, Tooltip } from "antd";
import type React from "react";
import styled from "styled-components";

const FieldWrapper = styled.div`
  margin-bottom: 32px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  margin-top: 6px;

  .ant-select-selector {
    min-height: 42px !important;
    padding: 4px 11px !important;
    border-radius: 6px !important;
    border: 1px solid var(--color-primary-desat) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: var(--color-primary-dark) !important;
    box-shadow: none !important;
  }
`;

export interface SingleSelectFieldProps {
  label: string;
  name: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: readonly string[];
  required?: boolean;
  tooltip?: string;
  placeholder?: string;
}

const SingleSelectField: React.FC<SingleSelectFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  tooltip,
  placeholder = "Select...",
}) => {
  const optionList = options.map((opt) => ({ value: opt, label: opt }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <StyledSelect
        id={name}
        placeholder={placeholder}
        value={value || undefined}
        onChange={(v) => onChange(typeof v === "string" ? v : undefined)}
        options={optionList}
        filterOption={(input, option) =>
          (option?.label ?? "")
            .toString()
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        showSearch
        allowClear
        aria-required={required}
      />
    </FieldWrapper>
  );
};

export default SingleSelectField;
